import './ChangePassword.scss';

import { ButtonV2, Input, Spinner, Toast } from '@travelwin/core';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import ForgottenPassword from '../../components/ForgottenPassword';
import Modal from '../../components/Modal';
import SuccessMessage from '../../components/SuccessMessage';
import { useUser } from '../../context/User';
import { getUserValidator } from '../../validation/userValidator';

const initialValues = {
  old_password: '',
  password: '',
  confirmPassword: '',
};

type ChangePasswordValues = typeof initialValues;

const className = 'c-ChangePassword';

interface Props {
  open: boolean;
  handleClose: () => void;
}

type VIEW_STATE = 'CHANGE_PASSWORD' | 'FORGOT_PASSWORD' | 'PASSWORD_CHANGED';

const ChangePassword = ({ open, handleClose }: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [viewState, setViewState] = useState<VIEW_STATE>('CHANGE_PASSWORD');
  const [hideCloseButton, setHideCloseButton] = useState(false);
  const [modalTitle, setModalTitle] = useState<string | undefined>(
    t('my_account.change_password', 'Change Password'),
  );

  const { updateUserPassword } = useUser();

  const userValidator = getUserValidator(t);
  const userSchema = yup.object().shape({
    old_password: yup
      .string()
      .required(t('login.password_required_error', 'A password is required.'))
      .max(
        256,
        t(
          'form.error.passwordMaxLength',
          "Your password can't be longer than {{maxLength}}",
          { maxLength: 256 },
        ),
      ),
    password: userValidator.password,
    confirmPassword: userValidator.confirmPassword,
  });

  const hasError = (
    props: FormikProps<ChangePasswordValues>,
    key: keyof ChangePasswordValues,
  ) => {
    return !!(props.errors[key] && props.touched[key]);
  };

  const getError = (
    props: FormikProps<ChangePasswordValues>,
    key: keyof ChangePasswordValues,
  ) => {
    return hasError(props, key) ? (
      <Toast error>{props.errors[key]}</Toast>
    ) : null;
  };

  const updatePassword = (
    { old_password, password }: ChangePasswordValues,
    { resetForm }: FormikHelpers<ChangePasswordValues>,
  ) => {
    setError(null);
    setIsLoading(true);
    updateUserPassword(old_password, password)
      .then(() => {
        resetForm();
        setViewState('PASSWORD_CHANGED');
        setHideCloseButton(true);
        setModalTitle(undefined);
      })
      .catch((err: Error) => {
        setError(err);
      })
      .finally(() => setIsLoading(false));
  };

  const renderResult = () => {
    if (error) {
      let message: string;
      switch (error.name) {
        case 'NotAuthorizedException':
          message = t(
            'signup.error.incorrectOldPassword',
            'Incorrect old password',
          );
          break;
        case 'InvalidPasswordException':
          message = t(
            'signup.error.invalidPassword',
            'Password did not conform with policy',
          );
          break;
        default:
          message = t(
            'error.generic',
            'An error has occurred, please try again later',
          );
      }
      return <Toast error>{message}</Toast>;
    }
  };

  const forgotPassword = () => {
    setViewState('FORGOT_PASSWORD');
    setModalTitle(undefined);
  };

  const renderView = () => {
    switch (viewState) {
      case 'CHANGE_PASSWORD':
        return (
          <Formik
            initialValues={initialValues}
            validationSchema={userSchema}
            onSubmit={updatePassword}
          >
            {(props) => (
              <form autoComplete="off" onSubmit={props.handleSubmit}>
                <div className={`${className}__content`}>
                  <div className={`${className}__instructions-text`}>
                    <p>
                      {t(
                        'change_password.instructions_text',
                        'To change your password, please enter the current password.',
                      )}
                    </p>
                  </div>

                  <Input
                    name="old_password"
                    data-testid="input-current-password"
                    label={t('form.old_password_label', 'Current Password')}
                    placeholder={t(
                      'form.old_password_label',
                      'Current Password',
                    )}
                    type="password"
                    autoComplete="password"
                    error={!!props.errors.old_password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      props.handleChange(e);
                      setError(null);
                    }}
                    onBlur={() => props.setFieldTouched('old_password', true)}
                    value={props.values.old_password}
                    addEyeIcon
                  />
                  {getError(props, 'old_password')}

                  <Input
                    name="password"
                    data-testid="input-new-password"
                    label={t('form.new_password_label', 'New Password')}
                    placeholder={t('form.password_placeholder', 'Password')}
                    type="password"
                    autoComplete="password"
                    error={!!props.errors.password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      props.handleChange(e);
                      setError(null);
                    }}
                    onBlur={() => props.setFieldTouched('password', true)}
                    value={props.values.password}
                    addEyeIcon
                  />
                  {getError(props, 'password')}

                  <Input
                    name="confirmPassword"
                    data-testid="input-confirm-password"
                    label={t('form.confirm_password_label', 'Confirm Password')}
                    placeholder={t('form.password_placeholder', 'Password')}
                    type="password"
                    autoComplete="password"
                    error={!!props.errors.confirmPassword}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      props.handleChange(e);
                      setError(null);
                    }}
                    onBlur={() =>
                      props.setFieldTouched('confirmPassword', true)
                    }
                    value={props.values.confirmPassword}
                    addEyeIcon
                  />
                  {getError(props, 'confirmPassword')}

                  {renderResult()}

                  <div className={`${className}__button-container`}>
                    <ButtonV2
                      data-testid="forgot-password"
                      variant="tertiary"
                      size="large"
                      fluid
                      onClick={forgotPassword}
                    >
                      {t('form.forgot_password', 'Forgot Your Password?')}
                    </ButtonV2>
                    <ButtonV2
                      data-testid="button-change-password"
                      variant="primary"
                      size="large"
                      disabled={
                        Object.keys(props.errors).length > 0 ||
                        (!props.dirty && !props.isSubmitting)
                      }
                      fluid
                      type="submit"
                    >
                      {t('form.reset_password', 'Reset my password')}{' '}
                      {isLoading && <Spinner size={12} color="white" />}
                    </ButtonV2>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        );
      case 'FORGOT_PASSWORD':
        return (
          <ForgottenPassword
            handleCompleted={() => {
              setViewState('PASSWORD_CHANGED');
              setHideCloseButton(true);
            }}
          />
        );
      case 'PASSWORD_CHANGED':
        return (
          <SuccessMessage
            title={t('change_password.congratulations', 'Congratulations')}
            description={t(
              'change_password.success_message',
              'Your password was changed successfully.',
            )}
            buttonText={t('change_password.continue_button', 'Continue')}
            buttonDataTestId="change-password-success-button"
            onButtonClick={handleClose}
          />
        );
    }
  };

  return (
    <Modal
      toggle={handleClose}
      on={open}
      hideCloseButton={hideCloseButton}
      title={modalTitle}
      fullScreen
    >
      <div className={className}>{renderView()}</div>
    </Modal>
  );
};

export default ChangePassword;
